import React from 'react';
import { useIntl } from 'react-intl';
import { CompanyLogo } from '../../../../../components/CompanyLogo';
import { ApplyButton } from '../components';
import { formatSrpSalaryDisplay } from '../../../../../utils/data/format-job-card-labels';
import { convertSalaryToMonthlyEUR } from '../utils/salaryConversion';
import { resolveSearchHitHostedLogoUrl } from '../../../../../utils/search/resolve-search-hit-company-display';
import type { DisplayJob } from '../../../../../types';

export interface BrandRailProps {
  displayJob: DisplayJob;
  panelColor: string;
  panelOn: string;
  applyUrl: string;
  isApplied: boolean;
  setAppliedJobState: (job: any, applied: boolean) => Promise<void>;
  onTailorCv?: () => void;
  formattedPostedDate: string | null;
  postedDaysAgo: number | null;
  formattedLastSeenDate: string | null;
  formattedApplicationDate: string | null;
  formattedEstimatedExpiryDate: string | null;
}

/**
 * Left rail for the redesigned (V2) job detail page: logo, fixed-height "(Est.) Monthly" +
 * salary slots (prevents jitter across jobs with different salary digit counts, per the design
 * handoff), a white "Apply now" pill, and posted/last-seen/expiry dates pinned to the bottom.
 */
export function BrandRail({
  displayJob,
  panelColor,
  panelOn,
  applyUrl,
  isApplied,
  setAppliedJobState,
  onTailorCv,
  formattedPostedDate,
  postedDaysAgo,
  formattedLastSeenDate,
  formattedApplicationDate,
  formattedEstimatedExpiryDate,
}: BrandRailProps): React.ReactElement {
  const intl = useIntl();
  const isEstimate = Boolean(displayJob.salaryIsEstimate);
  const hasRawSalary =
    typeof displayJob.salaryRangeMin === 'number' &&
    typeof displayJob.salaryRangeMax === 'number' &&
    displayJob.salaryRangeMin > 0 &&
    displayJob.salaryRangeMax > 0;
  // salaryRangeMin/Max on the job detail record are in the job's own currency/period
  // (e.g. USD/annual) — convert to monthly EUR before formatting, same as the rest of
  // this page (SalaryDistributionChart, +Page.tsx's useSalaryDisplay). formatSrpSalaryDisplay
  // is an SRP-card formatter that assumes its input is already monthly EUR; feeding it raw
  // values here produced e.g. "Monthly €500,000" for a $500k/year role.
  const convertedSalary = hasRawSalary
    ? convertSalaryToMonthlyEUR(
        displayJob.salaryRangeMin,
        displayJob.salaryRangeMax,
        displayJob.currency,
        displayJob.salaryPeriod,
      )
    : null;
  const hasSalary = convertedSalary !== null;
  const salaryLabel = convertedSalary
    ? formatSrpSalaryDisplay({
        salaryRangeMin: convertedSalary.min,
        salaryRangeMax: convertedSalary.max,
        salaryIsEstimate: displayJob.salaryIsEstimate,
        locale: intl.locale,
      })
    : null;

  const applyBtnStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    color: panelColor,
  };

  return (
    <div
      className="flex w-full flex-none flex-col items-center px-5 py-6 box-border lg:w-[210px] rounded-t-[24px] lg:rounded-l-[24px] lg:rounded-tr-none"
      style={{ background: panelColor, color: panelOn }}
    >
      <div className="flex h-[104px] w-[104px] flex-none items-center justify-center rounded-[22px] bg-white p-3.5 box-border overflow-hidden">
        <CompanyLogo
          companyName={displayJob.companyId || displayJob.companyName || ''}
          initialsSourceName={displayJob.companyName ?? displayJob.companyId}
          registryCompanyFormDescription={displayJob.registryCompanyFormDescription}
          isTyomarkkinatori={displayJob.isTyomarkkinatori}
          hostedLogoUrl={resolveSearchHitHostedLogoUrl(displayJob as unknown as Record<string, unknown>)}
          variant="bare"
          size={104}
          loading="eager"
        />
      </div>

      {hasSalary ? (
        <>
          <div className="mt-4 text-[10.5px] font-bold uppercase tracking-wide">
            {isEstimate
              ? intl.formatMessage({ id: 'hitV2.estMonthly', defaultMessage: 'Est. Monthly', description: 'Salary period label on the redesigned job card panel, estimated figure' })
              : intl.formatMessage({ id: 'hitV2.monthly', defaultMessage: 'Monthly', description: 'Salary period label on the redesigned job card panel' })}
          </div>
          <div className="mt-1.5 text-center [font-family:'Space_Grotesk',sans-serif] text-2xl font-bold leading-tight">
            {salaryLabel}
          </div>
        </>
      ) : null}

      <div className="mt-5 w-full">
        <ApplyButton
          applyUrl={applyUrl}
          isApplied={isApplied}
          displayJob={displayJob}
          applyBtnStyle={applyBtnStyle}
          applyBtnHoverStyle={{ filter: 'brightness(0.96)' }}
          effectiveBrandColour={panelColor}
          onSetApplied={setAppliedJobState}
          onTailorCv={onTailorCv}
          compact
        />
      </div>

      {(formattedPostedDate || formattedLastSeenDate || formattedApplicationDate || formattedEstimatedExpiryDate) ? (
        <div className="mt-auto flex flex-col items-center gap-1.5 pt-6 text-center text-[10.5px] font-semibold">
          {formattedPostedDate ? (
            <span>
              {intl.formatMessage(
                { id: 'jobDetailPageV2.postedOn', defaultMessage: 'Posted {date}', description: 'Posted date shown in the redesigned job detail page brand rail' },
                { date: formattedPostedDate },
              )}
              {postedDaysAgo != null ? ` · ${intl.formatMessage({ id: 'jobDetailPageV2.daysAgo', defaultMessage: '{days, plural, one {# day ago} other {# days ago}}', description: 'Relative days-ago label' }, { days: postedDaysAgo })}` : ''}
            </span>
          ) : null}
          {formattedLastSeenDate ? (
            <span>
              {intl.formatMessage(
                { id: 'jobDetailPageV2.lastSeen', defaultMessage: 'Last seen {date}', description: 'Last-seen-in-crawl date shown in the redesigned job detail page brand rail' },
                { date: formattedLastSeenDate },
              )}
            </span>
          ) : null}
          {formattedApplicationDate ? (
            <span>
              {intl.formatMessage(
                { id: 'jobDetailPageV2.deadline', defaultMessage: 'Deadline {date}', description: 'Application deadline shown in the redesigned job detail page brand rail' },
                { date: formattedApplicationDate },
              )}
            </span>
          ) : formattedEstimatedExpiryDate ? (
            <span>
              {intl.formatMessage(
                { id: 'jobDetailPageV2.estimatedExpiry', defaultMessage: 'Est. expiry {date}', description: 'Estimated listing expiry date shown in the redesigned job detail page brand rail' },
                { date: formattedEstimatedExpiryDate },
              )}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
